/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import { Suspense, useEffect, useRef } from "react";

import * as React from "react";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { Provider as ReduxProvider } from "react-redux";
import store from "reducers/Store";
import AppRouter from "components/AppRouter";
import { HoverAnimationProvider } from "components/shared/HoverAnimation";
import { Helmet } from "react-helmet";
import { useCurrentUser } from "actions/CurrentUserActions";
import {
  saveUserSettings,
  getUserLocale,
} from "utilities/UserSettingsUtilities";
import WebGPUSurvey from "./WebGPUSurvey";
import PurchaseExpressQueryParam from "./PurchaseExpressQueryParam";
import AppLoading from "components/AppLoading";

const UserSettings = (): React.ReactElement => {
  const { currentUser } = useCurrentUser();
  const lastSetLocale = useRef<string | null>(null);
  useEffect(() => {
    const locale = getUserLocale();
    // This will only fire if the user is loaded and the locale is set
    if (currentUser && locale && locale !== lastSetLocale.current) {
      saveUserSettings({ locale })
        .then(() => (lastSetLocale.current = locale))
        .catch((error) => {
          console.error("Error setting user locale:", error);
          newrelic.noticeError(error);
        });
    }
  }, [currentUser]);

  return <></>;
};

const AppRoot = (): React.ReactElement => {
  return (
    <Suspense fallback={<AppLoading />}>
      <ReduxProvider store={store}>
        <DndProvider backend={HTML5Backend}>
          <HoverAnimationProvider>
            <Helmet>
              <html lang={I18n.locale} />
              <title>{I18n.t("meta.title")}</title>
              <meta name="description" content={I18n.t("meta.description")} />
              <meta property="og:title" content={I18n.t("meta.title")} />
            </Helmet>
            <AppRouter />
            <UserSettings />
            <WebGPUSurvey />
            <PurchaseExpressQueryParam />
          </HoverAnimationProvider>
        </DndProvider>
      </ReduxProvider>
    </Suspense>
  );
};

export default AppRoot;
